<!--
  - Copyright (C) 2024. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div>
    <delete-dialog
      ref="deleteDialog"
      v-on:delete="onDeleteUser"
    ></delete-dialog>

    <sidebar
      class="sidebar"
      ref="useredit"
      :title="$t('users.common.user')"
      :subtitle="$t('users.common.editUser')"
    >
      <template v-slot:sidebar-content>
        <user-edit
          :init-user="editUser"
          :roles="roles"
          v-on:updated="updateUser"
        ></user-edit>
      </template>
    </sidebar>

    <sidebar
      class="sidebar"
      ref="passwordedit"
      v-on:closedEvent="wipePasswords"
      v-on:openEvent="wipePasswords"
      :title="$t('users.common.password')"
      :subtitle="$t('users.common.setPassword')"
    >
      <template v-slot:sidebar-content>
        <div>
          <h5 v-if="editUser">
            <b-icon-person></b-icon-person>
            {{ editUser.first_name }} {{ editUser.last_name }} ({{
              editUser.username
            }})
          </h5>
          <b-form>
            <b-form-group :label="$t('users.common.enterNewPassword') + ':'">
              <b-form-input
                type="password"
                v-model="password"
                :state="!password || password.length < 4 ? false : null"
                required
              ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('users.common.passwordMinLength') }}
              </div>
            </b-form-group>
            <b-form-group
              :label="$t('users.common.enterNewPasswordAgain') + ':'"
            >
              <b-form-input
                type="password"
                v-model="repeatedPassword"
                :state="
                  password && password !== repeatedPassword ? false : null
                "
                required
              ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('users.common.passwordMustMatch') }}
              </div>
            </b-form-group>
            <b-form-checkbox v-model="isTempPassword">
              {{ $t('users.common.tempPassword') }}
            </b-form-checkbox>
            <hr />
          </b-form>
          <b-button
            :disabled="
              !password ||
              password !== repeatedPassword ||
              pwIsLoading ||
              password.length < 4
            "
            variant="success"
            block
            @click="onSavePassword(editUser.id, password)"
          >
            {{ $t('users.common.save') }}
            <b-spinner small v-if="pwIsLoading"></b-spinner>
          </b-button>
        </div>
      </template>
    </sidebar>

    <!-- Secondary navigation-->
    <b-navbar toggleable="sm" type="light" variant="light">
      <b-navbar-nav class="ml-auto">
        <b-nav-item>
          <b-button class="mr-1" variant="primary" @click="onCreateUser">
            <b-icon-plus></b-icon-plus>
            <span class="d-sm-inline">
              {{ $t('users.common.createUser') }}</span
            >
          </b-button>
        </b-nav-item>
      </b-navbar-nav>
    </b-navbar>

    <b-container class="py-4" fluid>
      <div class="search-wrapper mb-5">
        <input
          type="text"
          v-model="searchQuery"
          ref="search"
          :placeholder="$t('ems.timeline.search')"
        />
      </div>

      <b-table
        hover
        head-variant="light"
        class="mt-3 row-pointer"
        :items="resultQuery"
        :fields="tableFields"
        :busy="isLoading"
        :sort-compare="sortTable"
        @row-clicked="onEditUser"
      >
        <template v-slot:cell(roles)="row">
          <b-badge
            variant="primary"
            class="mr-1"
            v-for="(role, i) in row.item.roles"
            :key="i"
          >
            {{ role.name }}
          </b-badge>
        </template>

        <template v-slot:cell(is_enabled)="row">
          <b-icon-check-square-fill
            v-if="row.item.is_enabled"
            variant="primary"
          ></b-icon-check-square-fill>
          <b-icon-square-fill v-else variant="secondary"></b-icon-square-fill>
        </template>

        <template v-slot:cell(online)="row">
          <b-icon-check-square-fill
            v-if="userIsOnline(row.item)"
            variant="primary"
          ></b-icon-check-square-fill>
          <b-icon-square-fill v-else variant="secondary"></b-icon-square-fill>
        </template>

        <template v-slot:cell(actions)="row">
          <b-button
            class="mb-1"
            size="sm"
            variant="dark"
            @click="onEditUser(row.item)"
          >
            <b-icon-pencil></b-icon-pencil>
          </b-button>
          <b-button
            class="mb-1 ml-1"
            size="sm"
            variant="dark"
            @click="onEditPassword(row.item)"
          >
            <b-icon-key></b-icon-key>
          </b-button>
          <b-button
            class="mb-1 ml-1"
            size="sm"
            variant="danger"
            @click="onDeleteDialog(row.item)"
          >
            <b-icon-trash></b-icon-trash>
          </b-button>
        </template>
      </b-table>
      <b-alert
        show
        v-if="resultQuery && !resultQuery.length"
        class="text-center"
      >
        <b-icon-exclamation-diamond></b-icon-exclamation-diamond>
        {{ $t('ems.common.nothingFound') }}
      </b-alert>
    </b-container>
    <div class="text-center">
      <small>{{ pkginfo.name }} v{{ pkginfo.version }}</small>
    </div>
  </div>
</template>

<script>
  import { Sidebar, utils } from '@amdx/emslib'
  import UserEdit from './UserEdit'
  import DeleteDialog from './DeleteDialog'
  import '@amdx/emslib/scss/globals.scss'
  import packageJson from '../package'

  export default {
    name: 'Users',
    components: {
      DeleteDialog,
      Sidebar,
      UserEdit
    },
    data () {
      return {
        isLoading: false,
        users: null,
        roles: null,
        editUser: null,
        tableData: null,
        searchQuery: null,
        pwIsLoading: false,
        password: null,
        repeatedPassword: null,
        isTempPassword: true,
        tableFields: [
          {
            key: 'username',
            label: this.$t('users.common.user'),
            sortable: true
          },
          {
            key: 'first_name',
            label: this.$t('users.common.firstName')
          },
          {
            key: 'last_name',
            label: this.$t('users.common.lastName')
          },
          {
            key: 'email',
            label: this.$t('users.common.email'),
            thClass: 'd-none d-lg-table-cell',
            tdClass: 'd-none d-lg-table-cell'
          },
          {
            key: 'roles',
            label: this.$t('users.common.roles'),
            sortable: true,
            thClass: 'd-none d-lg-table-cell',
            tdClass: 'd-none d-lg-table-cell'
          },
          {
            key: 'is_enabled',
            label: this.$t('users.common.enabled'),
            thClass: 'text-center',
            tdClass: 'text-center'
          },
          {
            key: 'online',
            label: this.$t('users.common.online'),
            thClass: 'text-center',
            tdClass: 'text-center'
          },
          {
            key: 'actions',
            label: 'Actions',
            thClass: 'text-right',
            tdClass: 'asset-text-cell align-middle text-right'
          }
        ]
      }
    },
    computed: {
      pkginfo () {
        return packageJson
      },
      resultQuery () {
        if (this.searchQuery) {
          return this.users.filter((item) => {
            return this.searchQuery
              .toLowerCase()
              .split(' ')
              .every(
                (v) =>
                  item.username.toLowerCase().includes(v) ||
                  item.last_name.toLowerCase().includes(v) ||
                  item.first_name.toLowerCase().includes(v) ||
                  item.email.toLowerCase().includes(v)
              )
          })
        } else {
          return this.users
        }
      }
    },
    watch: {
      password: function () {
        this.repeatedPassword = null
      }
    },
    methods: {
      update () {
        this.isLoading = true
        return this.$userManager
          .getUsers()
          .then(async (data) => {
            this.users = data.users
            let data_ = await this.$userManager.getRoles()
            this.roles = data_.roles
            for (let user of this.users) {
              let data_ = await this.$userManager.getUserRoles(user.id)
              user.roles = data_.roles.filter(
                (role) => !!this.roles.find((role_) => role_.id === role.id)
              )
              data_ = await this.$userManager.getUserSession(user.id)
              user.session = data_
            }
            this.isLoading = false
          })
          .catch((error) => {
            this.isLoading = false
            utils.toast(this, 'Keycloak response', error, 'danger')
          })
      },

      updateUser (username) {
        this.update().then(() => {
          this.editUser = this.users.find((u) => u.username === username)
        })
      },

      sortTable (a, b, key) {
        if (key === 'username') {
          return a.username.localeCompare(b.username)
        } else if (key === 'first_name') {
          return a.first_name.localeCompare(b.first_name)
        } else if (key === 'last_name') {
          return a.last_name.localeCompare(b.last_name)
        } else if (key === 'email') {
          return a.email.localeCompare(b.email)
        } else if (key === 'online') {
          return this.userIsOnline(a) > this.userIsOnline(b)
        } else if (key === 'roles') {
          let stringA = ''
          if (a.roles) {
            stringA = JSON.stringify(a.roles.map((g) => g.name))
          }
          let stringB = ''
          if (b.roles) {
            stringB = JSON.stringify(b.roles.map((g) => g.name))
          }
          return stringA.localeCompare(stringB)
        }
        return null
      },

      wipePasswords () {
        this.password = null
        this.repeatedPassword = null
        this.isTempPassword = true
      },

      userIsOnline (user) {
        return !!(user.session && user.session.is_online)
      },

      onEditUser (user) {
        this.editUser = user
        this.$refs.useredit.open()
        this.$refs.passwordedit.close()
      },

      onCreateUser () {
        this.editUser = { roles: [] }
        this.$refs.useredit.open()
      },

      onDeleteDialog (user) {
        this.$refs.deleteDialog.showQuestion(user.username, user.id)
      },

      onDeleteUser (id) {
        this.isLoading = true
        this.$userManager
          .deleteUser(id)
          .then(() => {
            this.update()
          })
          .catch((error) => {
            this.isLoading = false
            utils.toast(this, 'Keycloak response', error, 'danger')
          })
      },

      onEditPassword (user) {
        this.editUser = user
        this.$refs.passwordedit.open()
        this.$refs.useredit.close()
      },

      onSavePassword (userId, password) {
        this.pwIsLoading = true
        this.$userManager
          .setUserPassword(userId, password, this.isTempPassword)
          .then(() => {
            this.$refs.passwordedit.close()
            utils.toast(this, 'Keycloak response ', 'OK! Password set.')
            this.pwIsLoading = false
          })
          .catch((error) => {
            this.pwIsLoading = false
            utils.toast(this, 'Keycloak response', error, 'danger')
          })
      }
    },
    mounted () {
      this.update()
    }
  }
</script>

<style lang="scss" scoped></style>
